import { Box, IconButton } from '@youversion/design-system-components';
import CheckmarkStroked from '@youversion/design-system-icons/lib/CheckmarkStroked';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import Modal from 'components/ui/modal';
import ModalHeader from 'components/ui/modal/Header';
import { useQueryParams } from 'hooks/useQueryParams';
import { useSelectorEvents } from 'hooks/useSelectorEvents';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo } from 'react';
import { firebaseLogEvent } from 'utils/firebase';
import { getLanguageName } from 'utils/helpers/languageName';

import GiveOptionListItem from '../controls/GiveOptionListItem';
import RadioListItem from '../controls/RadioListItem';
import ModalFooter from '../modal/Footer';

interface Props {
  onClose: () => void;
}

function capitalize(lang: string) {
  return lang.slice(0, 1).toUpperCase() + lang.slice(1);
}

export default function LanguageSelector({ onClose }: Props) {
  const { i18n, t } = useTranslation('common');
  const { language: currentLanguage } = i18n;
  const router = useRouter();
  const { hide_nav: hideNav } = useQueryParams();
  const { logSelectorDismissedEvent, logSelectorPresentedEvent } = useSelectorEvents('giving_language');

  const locales = useMemo(
    () => router.locales ?? ([currentLanguage] as Array<string>),
    [currentLanguage, router.locales]
  );

  const languageNames = useMemo(() => {
    return locales.reduce(
      (obj, locale) => {
        obj[locale] = getLanguageName(locale);
        return obj;
      },
      {} as { [key: string]: string }
    );
  }, [locales]);

  function handleLanguageItemClick(locale: string) {
    const path = router.asPath;
    firebaseLogEvent('GivingButtonTap', { label: 'Language', screen: 'giving_language', value: locale });
    return router.push(path, path, { locale });
  }

  useEffect(() => {
    firebaseLogEvent('GivingScreenView', { screen: 'giving_language' });
    logSelectorPresentedEvent(currentLanguage);
    return () => void logSelectorDismissedEvent(currentLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal onClose={onClose}>
      <Box className='flex flex-col h-full'>
        <ModalHeader
          left={
            <IconButton
              className='inline-end-2 block-start-2'
              data-testid='close-modal-button'
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Close', screen: 'giving_language' });
                onClose();
              }}
            >
              <XStroked className='text-text-light dark:text-text-dark' />
              <label className='sr-only'>{t('giving.cta.close')}</label>
            </IconButton>
          }
          title={t('giving.titles.select_language')}
        />
        {hideNav ? (
          <div className='pbs-6'>
            <Box className='grow overflow-x-auto pli-3'>
              {locales.map(value => (
                <Box className='w-full' key={value}>
                  <GiveOptionListItem
                    className={currentLanguage === value ? 'bg-blue-10 dark:bg-gray-30' : ''}
                    id={`btn_locale_list_item_${value}`}
                    label={capitalize(languageNames[value] ?? value)}
                    onClick={() => handleLanguageItemClick(value)}
                    rightElement={
                      currentLanguage === value ? (
                        <CheckmarkStroked className='text-text-light dark:text-text-dark' />
                      ) : undefined
                    }
                    value={value}
                  />
                </Box>
              ))}
            </Box>{' '}
          </div>
        ) : (
          <>
            <Box className='grow overflow-x-auto pli-2'>
              {locales.map(value => (
                <Box className='w-full' key={value}>
                  <RadioListItem
                    className='mlb-2'
                    id={`btn_locale_list_item_${value}`}
                    isChecked={currentLanguage === value}
                    label={capitalize(languageNames[value] ?? value)}
                    onChange={() => handleLanguageItemClick(value)}
                    value={value}
                  />
                </Box>
              ))}
            </Box>
            <ModalFooter
              buttonText={t('giving.cta.done')}
              onClick={() => {
                firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_language' });
                onClose();
              }}
            />
          </>
        )}
      </Box>
    </Modal>
  );
}
